import {CheckIn, SchoolClassCheckIn, User} from "models"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import AppHistory from "shared/history"
import Button from "components/inputs/button"
import ChalkyLayout from "components/chalky-layout"
import {digg} from "diggerize"
import env from "shared/env"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import LayoutBase from "components/layout/base"
import {loading} from "components/layout/loading-blocker"
import memo from "set-state-compare/build/memo"
import React from "react"
import Routes from "shared/routes"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import useModel from "@kaspernj/api-maker/build/use-model"

/** School class check-in lobby route. */
export default memo(shapeComponent(class RoutesUserSchoolClassCheckInsLobby extends ShapeComponent {
  /** @returns {void} */
  setup() {
    const {locale, t} = useI18n({namespace: "js.routes.user.school_class_check_ins.lobby"})
    const {schoolClassCheckIn, schoolClassCheckInId} = useModel(SchoolClassCheckIn, {
      abilities: {
        SchoolClassCheckIn: ["stateEventCancel", "stateEventClose"]
      },
      eventUpdated: true,
      match: this.p.match,
      select: {
        SchoolClassCheckIn: ["code", "completedTeamSurveysCount", "id", "qrcodeUrlPath", "schoolClassId", "state", "stateMayCancel", "stateMayClose"]
      }
    })

    this.setInstance({
      currentUser: useCurrentUser(),
      locale,
      schoolClassCheckIn,
      schoolClassCheckInId,
      t
    })
  }

  /** @returns {React.ReactNode} */
  render() {
    const {currentUser, locale, schoolClassCheckIn, t} = this.tt

    this.layoutBaseDataSet ||= {route: "user/school-class-check-ins/lobby"}

    if (!currentUser) {
      return <ChalkyLayout dataSet={this.tt.layoutBaseDataSet} />
    }

    let playDomain
    const playDomainEnvKey = `PLAY_DOMAIN_${locale.toUpperCase()}`

    if (env[playDomainEnvKey]) {
      playDomain = digg(env, playDomainEnvKey)
    } else {
      playDomain = digg(env, "PLAY_DOMAIN")
    }

    return (
      <LayoutBase dataSet={this.tt.layoutBaseDataSet} headerTitle={CheckIn.modelName().human()} layout="user">
        {schoolClassCheckIn &&
          <div className="lobby-outer-container">
            <div className="lobby-container grid grid-cols-2">
              <div className="check-in-info col-span-2 col-span-lg-1">
                <div>
                  <div className="check-in-begins-container">
                    {t(".check_in_begins")}
                  </div>
                  {schoolClassCheckIn &&
                    <img className="qr-code-image" src={schoolClassCheckIn.qrcodeUrlPath()} />
                  }
                  <div className="enter-code-text">
                    {t(".go_to_play_wooftech_and_enter_this_code", {play_domain: playDomain})}
                  </div>
                  <div className="check-in-url-container">
                    {schoolClassCheckIn?.code()}
                  </div>
                </div>
              </div>
              <div className="check-in-status col-span-2 col-span-lg-1">
                <div>
                  <div className="check-in-done-count-container">
                    {schoolClassCheckIn.completedTeamSurveysCount()}
                  </div>
                  <div className="is-done-container">
                    {(schoolClassCheckIn.completedTeamSurveysCount() == 0 || schoolClassCheckIn.completedTeamSurveysCount() >= 2) &&
                      t(".are_done")
                    }
                    {schoolClassCheckIn.completedTeamSurveysCount() == 1 &&
                      t(".is_done")
                    }
                  </div>
                  <div className="close-woof-and-view-results-button-container">
                    {(() => {
                      if (
                        schoolClassCheckIn.completedTeamSurveysCount() >= 4 &&
                        schoolClassCheckIn.can("stateEventClose") &&
                        schoolClassCheckIn.stateMayClose()
                      ) {
                        return <Button
                          className="close-woof-and-view-results-button"
                          label={t(".close_woof_and_view_results")}
                          onClick={this.tt.closeWoofAndViewResultsClicked}
                          primary
                          white
                        />
                      } else {
                        return (
                          <>
                            <div>
                              {t(".not_enough_answers_to_view_results")}
                            </div>
                            {schoolClassCheckIn.can("stateEventCancel") && schoolClassCheckIn.stateMayCancel() &&
                              <a className="cancel-check-in-button" href="#" onClick={this.tt.onCancelCheckInClicked}>
                                {t(".click_here_to_cancel_woof")}
                              </a>
                            }
                          </>
                        )
                      }
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      </LayoutBase>
    )
  }

  /** @param {Event} e */
  closeWoofAndViewResultsClicked = async (e) => {
    e.preventDefault()

    const {currentUser, schoolClassCheckIn, schoolClassCheckInId} = this.tt
    const insightPath = Routes.userSchoolClassSchoolClassCheckInInsightsPath(schoolClassCheckIn.schoolClassId(), schoolClassCheckIn.id(), {
      school_class_check_ins_q: {id_eq: schoolClassCheckInId}
    })

    if (currentUser && currentUser.conferenceModeInWoofsClosingWithDelayAfter24Hours()) {
      AppHistory.push(insightPath)

      return
    }

    await loading(this.tt.t("js.shared.loading"), async () => {
      try {
        await User.stateEvent({
          model: schoolClassCheckIn,
          state_event: "close"
        })
        AppHistory.push(insightPath)
      } catch (error) {
        FlashMessage.errorResponse(error)
      }
    })
  }

  /** @param {Event} e */
  onCancelCheckInClicked = async (e) => {
    e.preventDefault()

    const {schoolClassCheckIn, t} = this.tt

    if (!confirm(t("js.shared.are_you_sure"))) return

    await loading(t("js.shared.loading"), async () => {
      try {
        await User.stateEvent({
          model: schoolClassCheckIn,
          state_event: "cancel"
        })
        FlashMessage.success(t("js.components.school_class_check_ins.close_button.the_woof_was_cancelled"))
        AppHistory.push(Routes.userSchoolClassClassSpacePath(schoolClassCheckIn.schoolClassId()))
      } catch (error) {
        FlashMessage.errorResponse(error)
      }
    })
  }
}))
